import type { IntercomProps } from "react-use-intercom";
import { getWebSupportProof, WebSupportProof } from "./supportIdentityService";

export type SupportStatus = "auth-resolving" | "transitioning" | "guest" | "verified" | "support-error" | "unavailable";
export interface SupportOwner {
  key: string;
  actorId: string | null;
  isCurrent: () => boolean;
}
export interface SupportSdk {
  ready: (signal: AbortSignal) => Promise<void>;
  boot: (props: IntercomProps, reboot: boolean) => void;
  update: (props: IntercomProps) => void;
  shutdown: () => void;
  clearIdentity: () => void;
  hasSession: () => boolean;
  show: () => void;
  hide: () => void;
  trackEvent: (name: string, metadata?: Record<string, unknown>) => void;
}

type Attributes = Record<string, string | number | boolean | undefined>;

// The SDK flattens custom attributes last. Never let callers override authentication
// or the canonical contact profile owned by notification-service.
export function safeSupportAttributes(attributes: Attributes = {}): Attributes {
  const reserved = /^(?:app_?id|api_?base|user_?id|external_?id|intercom_?user_?jwt|user_?hash|email|name|avatar|created_?at|signed_?up_?at|session_?duration|hide_?default_?launcher|custom_?attributes|company|companies)$/i;
  return Object.fromEntries(Object.entries(attributes).filter(([name, value]) =>
    !reserved.test(name) && (value === undefined || ["string", "number", "boolean"].includes(typeof value))
  ));
}

/** Owns one browser Messenger identity. JWTs and pending operations exist only in memory. */
export class IntercomSession {
  private owner: SupportOwner | null = null;
  private attributes: Attributes = {};
  private proof: WebSupportProof | null = null;
  private operation: Promise<boolean> | null = null;
  private controller = new AbortController();
  private booted: "guest" | "verified" | null = null;
  private observedSessionCookie = false;
  private lastBootAt = 0;
  private status: SupportStatus = "auth-resolving";
  private listeners = new Set<() => void>();
  private pendingUpdate: IntercomProps | null = null;
  private updateTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(
    private readonly sdk: SupportSdk,
    private readonly getProof = getWebSupportProof,
    private readonly report: (outcome: string, durationMs: number, refresh: boolean) => void = () => undefined
  ) {}

  public getStatus = (): SupportStatus => this.status;
  public subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => { this.listeners.delete(listener); };
  };

  private publish(status: SupportStatus): void {
    this.status = status;
    // Hide both the launcher and an open frame during an identity transition.
    document.documentElement.toggleAttribute("data-support-blocked", status !== "guest" && status !== "verified");
    this.listeners.forEach(listener => listener());
  }

  public setOwner(owner: SupportOwner | null, attributes: Attributes): void {
    this.attributes = safeSupportAttributes(attributes);
    if (owner?.key === this.owner?.key && owner !== null) return;
    this.invalidate();
    this.owner = owner;
    this.publish(owner ? "transitioning" : "auth-resolving");
    if (owner) void this.ensureReady();
  }

  /** Runs synchronously at the beginning of logout, before its network request settles. */
  public suspend = (): void => {
    this.invalidate();
    this.owner = null;
    this.publish("auth-resolving");
  };

  private invalidate(): void {
    this.controller.abort();
    this.controller = new AbortController();
    this.operation = null;
    this.proof = null;
    this.pendingUpdate = null;
    if (this.updateTimer) clearTimeout(this.updateTimer);
    this.updateTimer = null;
    try { this.sdk.hide(); } catch { /* Cleanup must not interrupt Plutus logout. */ }
    if (this.booted === "verified") this.shutdownIdentity();
  }

  private shutdownIdentity(): void {
    try {
      this.sdk.shutdown();
    } catch {
      // A broken vendor script cannot prevent cookie cleanup or Plutus logout.
    } finally {
      this.sdk.clearIdentity();
      this.booted = null;
      this.observedSessionCookie = false;
    }
  }

  public owns(key: string): boolean {
    return this.owner?.key === key && this.owner.isCurrent();
  }

  public captureOwner(): () => boolean {
    const owner = this.owner;
    return () => !!owner && this.owner === owner && owner.isCurrent();
  }

  private async ensureReady(retry = false): Promise<boolean> {
    const owner = this.owner;
    if (!owner || !owner.isCurrent() || (this.status === "support-error" && !retry)) return false;
    if (this.operation) return this.operation;
    const signal = this.controller.signal;
    const isCurrent = () => !signal.aborted && this.owner === owner && owner.isCurrent();
    const refresh = this.booted === "verified";
    const startedAt = Date.now();
    const operation = (async () => {
      try {
        if (retry) this.publish("transitioning");
        await this.sdk.ready(signal);
        if (!isCurrent()) return false;
        if (owner.actorId && (!this.proof || Date.parse(this.proof.expiresAtUtc) <= Date.now() + 15_000)) {
          const proof = await this.getProof(owner.actorId, signal);
          if (!isCurrent()) return false;
          this.proof = proof;
          this.report("proof_issued", Date.now() - startedAt, refresh);
        }
        if (!isCurrent()) return false;
        const hasSession = this.sdk.hasSession();
        const cookieExpired = !hasSession && (this.observedSessionCookie
          || (this.booted === "verified" && Date.now() - this.lastBootAt > 5_000));
        const nextMode = owner.actorId ? "verified" : "guest";
        if (!this.booted || this.booted !== nextMode || cookieExpired) {
          this.sdk.boot({
            hideDefaultLauncher: false,
            customAttributes: this.attributes,
            ...(this.proof ? { userId: owner.actorId!, intercomUserJwt: this.proof.proof } : {}),
          }, this.booted !== null);
          this.booted = nextMode;
          this.lastBootAt = Date.now();
          this.report(cookieExpired ? "cookie_renewed" : "boot_submitted", Date.now() - startedAt, refresh);
        }
        this.observedSessionCookie = this.sdk.hasSession();
        this.publish(nextMode);
        return true;
      } catch {
        if (!isCurrent()) return false;
        // Do not expose an anonymous Messenger or log upstream errors/payloads on failure.
        if (this.booted === "verified") this.shutdownIdentity();
        else {
          // Retain anonymous continuity if promotion failed before identifying anyone.
          try { this.sdk.shutdown(); } catch { /* Vendor failure is contained. */ }
          this.booted = null;
        }
        this.proof = null;
        this.publish("support-error");
        this.report("unavailable", Date.now() - startedAt, refresh);
        return false;
      }
    })();
    this.operation = operation;
    try {
      return await operation;
    } finally {
      if (this.operation === operation) this.operation = null;
    }
  }

  public show = (): void => {
    const current = this.captureOwner();
    void this.ensureReady(true).then(ready => {
      if (ready && current()) {
        try { this.sdk.show(); } catch { this.publish("support-error"); }
      }
    });
  };

  public hide = (): void => {
    try { this.sdk.hide(); } catch { /* Vendor failures do not affect navigation. */ }
  };

  /** Cookie expiry and JWT expiry are independent; never reboot on a fixed JWT timer. */
  public resume = (): void => { void this.ensureReady(); };

  public update = (props: IntercomProps): void => {
    if (!this.owner?.isCurrent() || this.status === "support-error") return;
    this.pendingUpdate = {
      ...this.pendingUpdate,
      customAttributes: { ...this.pendingUpdate?.customAttributes, ...safeSupportAttributes(props.customAttributes) },
      ...(props.horizontalPadding === undefined ? {} : { horizontalPadding: props.horizontalPadding }),
    };
    if (this.updateTimer) return;
    // Merge page/theme/questionnaire changes and panel drag frames into one update.
    this.updateTimer = setTimeout(() => { void this.flushUpdate(); }, 250);
  };

  private async flushUpdate(): Promise<void> {
    this.updateTimer = null;
    const current = this.captureOwner();
    const props = this.pendingUpdate;
    this.pendingUpdate = null;
    if (!props || !await this.ensureReady() || !current()) return;
    try {
      this.sdk.update({ ...props, ...(this.proof ? { userId: this.owner!.actorId!, intercomUserJwt: this.proof.proof } : {}) });
    } catch {
      this.shutdownIdentity();
      this.proof = null;
      this.publish("support-error");
    }
  }

  public trackEvent = (name: string, metadata?: Record<string, unknown>): void => {
    if (!this.owner?.isCurrent() || !["verified", "guest"].includes(this.status)) return;
    const current = this.captureOwner();
    void this.ensureReady().then(ready => {
      if (!ready || !current()) return;
      try { this.sdk.trackEvent(name, metadata); } catch { /* Optional tracking never blocks support. */ }
    });
  };

  public dispose(): void {
    this.suspend();
    try { this.sdk.shutdown(); } catch { /* Unmount must remain safe. */ }
    this.booted = null;
  }
}
